// Correction DÉTERMINISTE des exercices Reps.
// LOT 2 : correction côté client (le contenu local contient la solution).
// TODO (prod) : déplacer cette correction côté serveur et NE PAS envoyer
// `solution` au client. La fonction est isolée ici pour faciliter ce basculement.

// Normalisation du TEXTE libre (type_answer, et le `contains` des regles dom) :
// on juge le FOND, pas la facon dont le clavier a ecrit la reponse. Miroir EXACT
// de RepsGrader::normalizeText (PHP) et du grader mobile : toute evolution se
// fait DANS LES TROIS, dans le meme ordre, sinon l'apprenant voit vert hors
// ligne et rouge au submit (ou l'inverse). Les caracteres sont ecrits en
// echappements \u et jamais en clair : les trois moteurs doivent lister
// exactement les memes points de code, et un blanc exotique ecrit en clair est
// invisible a la relecture.
// `strict` (posé par `solution.strict: true`) rend la CASSE significative, pour
// les rares étapes dont la casse EST la notion : « qu'affiche
// print("promo".upper()) ? ». Partout ailleurs on juge le fond.
export function normalizeText(s, strict) {
    const base = (s || "").toString();
    return (
        (strict ? base : base.toLowerCase())
            .trim()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // retire les accents
            // LE CLAVIER SUBSTITUE TOUT SEUL. iOS remplace l'apostrophe droite
            // par la typographique (U+2019) et les guillemets par des courbes
            // pendant la frappe : « l'interligne » devenait « l\u2019interligne »
            // et `'w'` devenait `\u2019w\u2019`. Bonne reponse, verdict faux,
            // aucun signal. Les variantes courbes se ramenent aux droites.
            // Le clavier FR va plus loin (mesure au simulateur, 26 aout
            // 2026) : la touche guillemet insere des CHEVRONS \u00ab \u00bb,
            // avec une espace insecable que la passe des blancs absorbe deja.
            .replace(/[\u2018\u2019\u201a\u201b\u2032\u02bc\u00b4`\u2039\u203a]/g, "'")
            .replace(/[\u00ab\u00bb\u201c\u201d\u201e\u201f\u2033]/g, '"')
            .replace(/[\u2010-\u2015\u2212]/g, "-") // tirets typographiques
            .replace(/[<>/]/g, "")
            // Blancs listes EXPLICITEMENT : `\s` ne recouvre pas les memes
            // caracteres en JS et en PCRE (l'espace insecable en fait partie ici
            // et pas forcement la-bas). Une reponse copiee-collee qui en contient
            // une donnait donc un verdict client et un verdict serveur
            // differents, le pire des cas.
            .replace(
                /[\s\u0085\u00a0\u1680\u180e\u2000-\u200b\u2028\u2029\u202f\u205f\u3000\ufeff]+/g,
                ""
            )
            // Trait d'union ENTRE DEUX LETTRES uniquement : « font-weight » (la
            // graphie officielle de la propriete, celle que tape l'apprenant qui
            // connait son CSS) et « font weight » deviennent la meme reponse.
            // Jamais ailleurs : « -2 » et « ventes[-2] » doivent rester
            // differents de « 2 », sinon l'index negatif de Python serait
            // accepte a l'envers.
            // Insensible a la casse : en mode strict le texte n'a pas ete
            // minuscule, et « Font-Weight » doit se replier comme « font-weight ».
            .replace(/([a-z])-+(?=[a-z])/gi, "$1")
            // PONCTUATION DE FIN DE PHRASE. Un point oublie ou en trop n'est
            // pas une faute de fond : « merci pour ta commande » vaut « merci
            // pour ta commande ! ». Restreint a . ! ? — les deux-points et le
            // point-virgule sont de la SYNTAXE (fin d'en-tete de bloc Python,
            // fin de declaration CSS), les retirer validerait l'oubli le plus
            // classique du debutant. Les blancs ont deja tous disparu a ce
            // stade, la ponctuation est donc bien collee a la fin.
            .replace(/\u2026/g, "...")
            .replace(/[.!?]+$/, "")
    );
}

function arrEq(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
        return false;
    }
    return a.every((v, i) => v === b[i]);
}

// Miroir de is_numeric() PHP : nombre fini, ou chaîne numérique (espaces de
// tête tolérés, notation décimale/scientifique). Tout le reste est refusé.
function isNumericLike(v) {
    if (typeof v === "number") return Number.isFinite(v);
    if (typeof v !== "string") return false;
    return /^[ \t\n\r\v\f]*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/.test(v);
}

// Comparaison d'un index de réponse (mcq, find_error, predict_output) à l'index
// solution, EXACTEMENT comme le serveur (RepsGrader.php) : is_numeric des deux
// côtés puis cast entier. Sans cette garde, `undefined === undefined` validerait
// une solution absente, et un index string ("2") divergerait du verdict serveur.
function indexEq(answer, expected) {
    if (!isNumericLike(answer) || !isNumericLike(expected)) return false;
    return Math.trunc(Number(answer)) === Math.trunc(Number(expected));
}

// Normalisation du CODE (write_code) : on juge le FOND, pas la forme (indulgence
// facon BeBot). Minuscules, espaces entre balises supprimes, espaces autour de la
// ponctuation CSS/structurelle colles (color: red == color:red), espaces autour
// des virgules colles (clamp(1.75rem, 5vw, 3rem) == clamp(1.75rem,5vw,3rem)),
// espaces reduits. On NE retire PAS <>/ (ce sont les balises qu'on veut verifier).
export function normalizeCode(s) {
    return (s || "")
        .toString()
        .toLowerCase()
        .replace(/>\s+</g, "><")
        .replace(/\s*([:;{}])\s*/g, "$1")
        .replace(/\s*,\s*/g, ",")
        .replace(/\s+/g, " ")
        .trim();
}

// Normalisation de la SORTIE console (python_code) : on ignore les espaces en fin
// de ligne et les lignes vides en tête/fin, mais on garde la structure interne.
//
// On ignore AUSSI la casse et la ponctuation de fin de phrase : refuser
// « bienvenue chez moi » parce que l'apprenant n'a pas mis la majuscule, c'est
// lui apprendre que le produit est injuste, pas lui apprendre Python. Les rares
// leçons dont la casse EST la notion (`.upper()`, `True` / `False`, un nom
// d'exception) posent `solution.strict: true` — et le lint les y oblige.
export function normalizeOutput(s, strict) {
    const base = (s || "")
        .toString()
        // Mêmes fins de ligne dans les TROIS moteurs avant toute passe ancrée :
        // `$` reconnaît \r, U+2028 et U+2029 en JS, PCRE les ignore. Une sortie
        // contenant un \r seul donnait donc vert au client et rouge au serveur,
        // qui est celui qui décide de l'XP.
        .replace(/\r\n?/g, "\n")
        .replace(/[\u2028\u2029]/g, "\n")
        // Les formats locaux de JavaScript posent des espaces insécables
        // (`toLocaleString("fr-FR")` : U+202F entre les milliers, U+00A0 avant
        // le symbole de la monnaie). À l'œil ce sont des espaces : on les
        // compare comme tels, dans les trois correcteurs.
        .replace(/[\u00a0\u202f]/g, " ")
        .replace(/[ \t]+$/gm, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    if (strict) return base;
    return base
        .replace(/\u2026/g, "...")
        .replace(/[ \t]*[.!?]+$/, "")
        .toLowerCase();
}

// Présence d'un motif include/forbid dans le code normalisé. Un motif qui est
// une balise ouvrante SIMPLE (`<ul>`) matche aussi la balise avec attributs
// (`<ul class="menu">`) : un élève qui fait plus que demandé n'est jamais
// puni (indulgence sur la forme). Tout autre motif reste un littéral.
// Miroir EXACT de RepsGrader::codeIncludesPattern (PHP).
export function codeIncludesPattern(code, pattern) {
    const m = /^<([a-z][a-z0-9-]*)>$/.exec(pattern);
    if (m) {
        return new RegExp(`<${m[1]}(>|\\s[^>]*>)`).test(code);
    }
    return code.includes(pattern);
}

// Correction STRUCTURELLE (write_code) : solution.dom = règles par sélecteur
// CSS sur le HTML réellement parsé. Zéro faux positif d'imbrication (les
// motifs texte ne voient pas la structure), zéro faux négatif d'attributs.
// Règle : { selector, count? (exact), min?, max?, contains? (au moins un
// élément matché contient ce texte, insensible casse/accents) } ; sans
// count/min/max, la règle exige au moins un élément. TOUTES les règles
// doivent passer. Sous-ensemble de sélecteurs garanti côté serveur : tags,
// .classe, #id, [attr], [attr=valeur], combinateurs « espace » et « > »
// (le lint et l'éditeur le font respecter). Miroir de RepsGrader (PHP,
// DOMDocument + XPath) : toute évolution se fait DES DEUX CÔTÉS.
export function gradeDomRules(html, rules) {
    if (typeof DOMParser === "undefined") return false; // navigateur requis
    let doc;
    try {
        doc = new DOMParser().parseFromString(
            (html || "").toString(),
            "text/html"
        );
    } catch (e) {
        return false;
    }
    return rules.every((rule) => {
        if (!rule || typeof rule.selector !== "string" || !rule.selector.trim()) {
            return false;
        }
        let nodes;
        try {
            // [attr=valeur] est accepte par linkedom, le serveur et le mobile,
            // mais un VRAI navigateur exige des guillemets des que la valeur
            // porte un point ou un slash (a[href=menu.html] -> SyntaxError,
            // donc regle infranchissable et bonne reponse comptee fausse,
            // invisible pour les harnais qui tournent sur linkedom). On cite
            // systematiquement : le sous-ensemble linte garantit une valeur
            // sans guillemets ni antislash.
            const sel = String(rule.selector).replace(
                /\[([a-zA-Z-]+)=([^"'\]]+)\]/g,
                '[$1="$2"]'
            );
            nodes = doc.querySelectorAll(sel);
        } catch (e) {
            return false; // sélecteur invalide = règle infranchissable
        }
        const n = nodes.length;
        if (rule.count !== undefined && n !== rule.count) return false;
        if (rule.min !== undefined && n < rule.min) return false;
        if (rule.max !== undefined && n > rule.max) return false;
        if (
            rule.count === undefined &&
            rule.min === undefined &&
            rule.max === undefined &&
            n < 1
        ) {
            return false;
        }
        if (rule.contains !== undefined) {
            const want = normalizeText(rule.contains);
            let found = false;
            for (const el of nodes) {
                if (normalizeText(el.textContent).includes(want)) {
                    found = true;
                    break;
                }
            }
            if (!found) return false;
        }
        return true;
    });
}

// Un motif `include` peut être un TABLEAU : « au moins une de ces écritures ».
// Indispensable dès qu'un même effet s'écrit de plusieurs façons légitimes
// (`background` et `background-color`) : sans ça, la bonne réponse d'un
// apprenant qui en sait un peu plus que la leçon est comptée fausse.
// PARTAGÉ par write_code ET python_code : la logique n'a vécu que dans
// write_code pendant un temps, et le mobile l'appliquait déjà aux deux — donc
// la même solution y était acceptée et refusée ici. Miroir EXACT de
// RepsGrader::includeSatisfied (PHP) et de codeSatisfiesPattern (mobile).
export function codeSatisfiesPattern(code, motif) {
    if (Array.isArray(motif)) {
        return motif
            .map(normalizeCode)
            .some((m) => m && codeIncludesPattern(code, m));
    }
    const m = normalizeCode(motif);
    return !m || codeIncludesPattern(code, m);
}

// LES RACCOURCIS CSS SE DÉPLIENT. `margin: 0 0 24px 0` FIXE la marge basse à
// 24px, exactement comme `margin-bottom: 24px` : refuser la première écriture
// parce que l'exercice attendait la seconde, c'est punir une bonne réponse. Le
// cas s'est produit sur l'unité 15 (« une marge basse de 24px »), et 96 motifs
// du parcours exigeaient une propriété détaillée qu'un raccourci satisfait.
// On AJOUTE donc les déclarations détaillées équivalentes ; on ne retire rien,
// donc aucun motif existant ne change de verdict.
// Sens unique, volontairement : un raccourci donne ses détails, mais un détail
// ne reconstitue pas le raccourci (`margin-bottom` seul ne dit rien du haut).
// Miroir EXACT de RepsGrader::shorthandParts (PHP) et du grader mobile.
export function shorthandParts(decl) {
    const i = String(decl).indexOf(":");
    if (i === -1) return [];
    const prop = decl.slice(0, i).trim().toLowerCase();
    const val = decl.slice(i + 1).trim();
    if (!val) return [];
    if (prop === "margin" || prop === "padding") {
        const p = val.split(/\s+/).filter(Boolean);
        // `auto`, calc(), var()… : on ne déplie que des valeurs simples.
        if (!p.length || p.length > 4 || p.some((x) => /[(,]/.test(x))) return [];
        const cotes =
            p.length === 1 ? [p[0], p[0], p[0], p[0]]
            : p.length === 2 ? [p[0], p[1], p[0], p[1]]
            : p.length === 3 ? [p[0], p[1], p[2], p[1]]
            : [p[0], p[1], p[2], p[3]];
        return [
            `${prop}-top: ${cotes[0]}`,
            `${prop}-right: ${cotes[1]}`,
            `${prop}-bottom: ${cotes[2]}`,
            `${prop}-left: ${cotes[3]}`,
        ];
    }
    if (prop === "border") {
        const p = val.split(/\s+/).filter(Boolean);
        if (!p.length || p.length > 3 || p.some((x) => /[(,]/.test(x))) return [];
        const STYLES = "none hidden dotted dashed solid double groove ridge inset outset".split(" ");
        let largeur = null;
        let style = null;
        let couleur = null;
        for (const x of p) {
            const bas = x.toLowerCase();
            if (STYLES.indexOf(bas) !== -1) style = bas;
            else if (/^[\d.]+(px|em|rem|%)?$/.test(bas) || bas === "thin" || bas === "medium" || bas === "thick") largeur = bas;
            else couleur = bas;
        }
        const out = [];
        if (largeur !== null) out.push(`border-width: ${largeur}`);
        if (style !== null) out.push(`border-style: ${style}`);
        if (couleur !== null) out.push(`border-color: ${couleur}`);
        return out;
    }
    if (prop === "background") {
        // Une couleur seule, rien d'autre : une image ou un dégradé ne se
        // ramène pas à background-color.
        if (/^(#[0-9a-f]{3,8}|[a-z]+|rgba?\([^)]*\)|hsla?\([^)]*\))$/i.test(val)) {
            return [`background-color: ${val}`];
        }
        return [];
    }
    return [];
}

// Déclarations CSS CANONIQUES d'un code : une entrée `selecteur{propriete:valeur;}`
// par couple (sélecteur, déclaration), quelle que soit la FORME d'écriture.
//
// On corrige le FOND, pas la forme : un apprenant qui obtient le même résultat
// par un autre chemin ne doit jamais être puni. Trois écritures produisent la
// même page et n'étaient pourtant pas reconnues par des motifs texte :
//   <style>h1 { color: red }</style>      (la seule qui passait)
//   <h1 style="color: red">               (attribut en ligne)
//   <style>h1 { font-size: 40px; color: red }</style>   (déclaration pas en tête)
//   <style>h1, h2 { color: red }</style>  (sélecteurs groupés)
// Le résultat est ajouté au code normalisé : les motifs existants continuent de
// matcher la forme d'origine, et matchent en plus toutes ces variantes.
// Miroir EXACT de RepsGrader::expandCssDeclarations (PHP) et du grader mobile.
export function expandCssDeclarations(raw) {
    const src = String(raw == null ? "" : raw);
    const out = [];
    const pousse = (selecteurs, declarations) => {
        for (const brut of String(declarations).split(";")) {
            const d = brut.trim();
            if (!d || d.indexOf(":") === -1) continue;
            const formes = [d].concat(shorthandParts(d));
            for (const un of String(selecteurs).split(",")) {
                const s = un.trim();
                if (!s) continue;
                for (const f of formes) out.push(`${s}{${f};}`);
            }
        }
    };
    // 1. règles écrites dans un bloc <style>. La fermeture est FACULTATIVE :
    // un navigateur applique la feuille de style même sans `</style>` (tout ce
    // qui suit devient du texte brut), et l'aperçu de la leçon le montre. Exiger
    // la balise fermante ici revenait à refuser une page qui s'affiche
    // parfaitement — l'apprenant voyait son résultat correct et un verdict faux.
    const blocs = /<style[^>]*>([\s\S]*?)(?:<\/style\s*>|$)/gi;
    let bloc;
    while ((bloc = blocs.exec(src))) {
        const css = bloc[1].replace(/\/\*[\s\S]*?\*\//g, "");
        const regles = /([^{}]+)\{([^{}]*)\}/g;
        let regle;
        while ((regle = regles.exec(css))) pousse(regle[1], regle[2]);
    }
    // 2. attributs style="..." posés directement sur une balise
    const enLigne = /<([a-zA-Z][\w-]*)\b[^>]*?\sstyle\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
    let el;
    while ((el = enLigne.exec(src))) {
        pousse(el[1], el[2] !== undefined ? el[2] : el[3] || "");
    }
    return out.join(" ");
}

// Déclarations canoniques d'un fichier CSS BRUT (un styles.css n'a pas de
// balise <style> : l'expansion ci-dessus ne verrait rien). Même sortie, même
// vocabulaire : un motif `p{color` matche que la règle vive dans un bloc
// <style>, un attribut style= ou un fichier à part — c'est exactement la
// promesse « on corrige le résultat, pas l'écriture » étendue au
// multi-fichiers. Miroir EXACT de RepsGrader (PHP) et du grader mobile.
export function expandRawCssDeclarations(cssText) {
    const css = String(cssText == null ? "" : cssText).replace(/\/\*[\s\S]*?\*\//g, "");
    const out = [];
    const regles = /([^{}]+)\{([^{}]*)\}/g;
    let regle;
    while ((regle = regles.exec(css))) {
        for (const brut of String(regle[2]).split(";")) {
            const d = brut.trim();
            if (!d || d.indexOf(":") === -1) continue;
            const formes = [d].concat(shorthandParts(d));
            for (const un of String(regle[1]).split(",")) {
                const s = un.trim();
                if (!s) continue;
                for (const f of formes) out.push(`${s}{${f};}`);
            }
        }
    }
    return out.join(" ");
}

// Surface de correction d'UN contenu : le code tel qu'écrit, plus ses
// déclarations CSS canoniques (selon que le contenu est une page ou une
// feuille de style). C'est là-dessus que courent include et forbid.
function codeSurface(raw, isCssFile) {
    return (
        normalizeCode(raw) +
        " " +
        normalizeCode(isCssFile ? expandRawCssDeclarations(raw) : expandCssDeclarations(raw))
    );
}

// Nom de fichier canonique et page d'accueil d'une réponse multi-fichiers.
// MIROIR de lib/reps/multifile.js (normalizeFileName / entryFileName) : le
// correcteur reste autonome (trois implémentations, PHP et mobile n'importent
// rien), mais les deux définitions ne doivent JAMAIS diverger.
function canonicalFileName(name) {
    return String(name == null ? "" : name).trim().replace(/^\.\//, "").toLowerCase();
}
function entryOf(names) {
    if (names.includes("index.html")) return "index.html";
    for (const n of names) {
        if (n.endsWith(".html")) return n;
    }
    return names.length ? names[0] : null;
}

// Un texte entre guillemets doubles dans une requête SQL. On retire d'abord
// les textes entre guillemets simples (`'il a dit "oui"'` est juste) et les
// commentaires, puis on cherche un `"` dans ce qui reste. Miroir EXACT de
// RepsGrader::sqlHasDoubleQuotes (PHP), de grade.ts (app) et de
// RepsSql.aDesGuillemetsDoubles (public/static/reps/sql-format.js).
export function sqlHasDoubleQuotes(code) {
    const reste = String(code || "")
        .replace(/'(?:[^']|'')*'/g, "''")
        .replace(/--[^\n]*/g, " ")
        .replace(/\/\*[\s\S]*?\*\//g, " ");
    return reste.indexOf('"') !== -1;
}

export function gradeExercise(exercise, answer) {
    const s = exercise.solution || {};
    switch (exercise.type) {
        case "mcq":
            return indexEq(answer, s.correctIndex);
        case "multi_select": {
            // answer = tableau des index cochés, dans l'ordre où l'apprenant a
            // coché (les cartes sont mélangées à l'affichage) : on compare des
            // ENSEMBLES, jamais des listes ordonnées. Tout ce qui est juste doit
            // être coché, et rien d'autre. Miroir EXACT de RepsGrader::grade.
            if (!Array.isArray(answer) || !Array.isArray(s.correctIndexes)) {
                return false;
            }
            const got = new Set(
                answer.filter(isNumericLike).map((v) => Math.trunc(Number(v))),
            );
            const want = new Set(
                s.correctIndexes.filter(isNumericLike).map((v) => Math.trunc(Number(v))),
            );
            if (got.size !== want.size) return false;
            for (const v of want) {
                if (!got.has(v)) return false;
            }
            return true;
        }
        case "find_error":
            // answer = index (0-based) de la ligne de code cliquée.
            return indexEq(answer, s.lineIndex);
        case "predict_output":
            // answer = index de l'option choisie (options mono façon mcq).
            return indexEq(answer, s.correctIndex);
        case "fill_blank":
            return arrEq(answer, s.blanks);
        case "tap_tokens":
            return arrEq(answer, s.sequence);
        case "reorder":
            return arrEq(answer, s.order);
        case "drop_blocks": {
            // answer = [index du bloc place par emplacement].
            // s.accept = liste d'agencements valides (ex. <strong><em> ET <em><strong>) ;
            // s.slots = raccourci pour un seul agencement.
            if (!Array.isArray(answer)) return false;
            const accepts = s.accept || (s.slots ? [s.slots] : []);
            return accepts.some((a) => arrEq(answer, a));
        }
        case "drag_drop":
            return arrEq(answer, s.placement);
        case "type_answer": {
            // `solution.strict` : la casse compte (leçon sur .upper(), sur
            // True/False, sur le nom exact d'une exception).
            const strict = s.strict === true;
            const accepted = (s.accepted || []).map((v) =>
                normalizeText(v, strict)
            );
            return accepted.includes(normalizeText(answer, strict));
        }
        case "match_pairs": {
            if (!answer) return false;
            return (s.pairs || []).every((p) => answer[p[0]] === p[1]);
        }
        case "write_code": {
            // Réponse MONO-fichier : le code, en chaîne. Réponse MULTI-FICHIERS
            // (leçons à data.files) : { files: { nom: contenu } }. Dans les deux
            // cas on accepte si TOUS les motifs `include` sont présents, AUCUN
            // `forbid`, toutes les règles `dom` satisfaites, et — multi
            // seulement — toutes les règles par fichier (solution.files).
            const multi =
                answer &&
                typeof answer === "object" &&
                answer.files &&
                typeof answer.files === "object" &&
                !Array.isArray(answer.files);
            const fichiers = {};
            if (multi) {
                for (const [n, c] of Object.entries(answer.files)) {
                    // Valeur non-chaine = fichier vide, dans les TROIS moteurs :
                    // les casts naturels divergent (String(true)="true" en JS,
                    // (string)true="1" en PHP) et briseraient la parite.
                    fichiers[canonicalFileName(n)] = typeof c === "string" ? c : "";
                }
            }
            const noms = Object.keys(fichiers);
            // include/forbid PLATS : sur la réunion de tous les fichiers. Un
            // motif `p{color` est satisfait où que la règle vive — page,
            // feuille à part, attribut. Imposer un EMPLACEMENT est le travail
            // de solution.files, jamais des motifs plats.
            // Toute reponse mono qui n'est pas une chaine est VIDE : un objet
            // inattendu donnerait String(answer)="[object Object]" en JS et
            // autre chose en PHP — le vide, lui, est identique partout.
            const code = multi
                ? noms.map((n) => codeSurface(fichiers[n], n.endsWith(".css"))).join(" ")
                : codeSurface(typeof answer === "string" ? answer : "", false);
            const inc = s.include || [];
            const forbid = (s.forbid || []).map(normalizeCode);
            if (!inc.every((m) => codeSatisfiesPattern(code, m))) {
                return false;
            }
            if (forbid.some((m) => m && codeIncludesPattern(code, m))) {
                return false;
            }
            // Règles de structure : sur la page d'ACCUEIL (index.html, sinon la
            // première page). Les autres pages se vérifient via solution.files.
            const dom = Array.isArray(s.dom) ? s.dom : [];
            if (dom.length > 0) {
                const cible = multi
                    ? fichiers[entryOf(noms)] || ""
                    : typeof answer === "string"
                      ? answer
                      : "";
                if (!gradeDomRules(cible, dom)) return false;
            }
            // Règles PAR FICHIER : quand l'énoncé impose un emplacement (« la
            // règle vit dans styles.css »), ou vise une page précise. Un
            // fichier attendu absent de la réponse = faux.
            const parFichier =
                s.files && typeof s.files === "object" && !Array.isArray(s.files)
                    ? s.files
                    : null;
            if (parFichier) {
                for (const [n, regles] of Object.entries(parFichier)) {
                    const nom = canonicalFileName(n);
                    const contenu = fichiers[nom];
                    if (contenu === undefined) return false;
                    const surface = codeSurface(contenu, nom.endsWith(".css"));
                    // Une regle malformee (include en chaine) ne doit jamais
                    // faire PLANTER le clic « Verifier » : on l'ignore, comme
                    // le serveur. Le lint la refuse en amont de toute facon.
                    const incF = Array.isArray(regles && regles.include) ? regles.include : [];
                    const forbF = (Array.isArray(regles && regles.forbid) ? regles.forbid : []).map(normalizeCode);
                    if (!incF.every((m) => codeSatisfiesPattern(surface, m))) {
                        return false;
                    }
                    if (forbF.some((m) => m && codeIncludesPattern(surface, m))) {
                        return false;
                    }
                    const domF = Array.isArray(regles && regles.dom) ? regles.dom : [];
                    if (domF.length > 0 && !gradeDomRules(contenu, domF)) return false;
                }
            }
            // APERÇU VIVANT (parcours JavaScript) : `solution.rendered` = les
            // mêmes règles par sélecteur, appliquées à la page APRÈS ses
            // scripts et les gestes de `data.actions`. Cette page-là n'existe
            // que dans le navigateur de l'apprenant : le player l'envoie dans
            // `answer.rendered`, comme il envoie déjà `stdout`. Le lint exige
            // qu'une telle étape pose AUSSI un motif de code sur un fichier
            // .js : une photo sans le code qui la produit ne suffit pas.
            const rendu = Array.isArray(s.rendered) ? s.rendered : [];
            if (rendu.length > 0) {
                const photo =
                    answer && typeof answer === "object" && typeof answer.rendered === "string"
                        ? answer.rendered
                        : "";
                if (!gradeDomRules(photo, rendu)) return false;
            }
            return (
                inc.length > 0 ||
                dom.length > 0 ||
                rendu.length > 0 ||
                (parFichier != null && Object.keys(parFichier).length > 0)
            );
        }
        // js_code : le jumeau JavaScript, même réponse et même correction.
        case "python_code":
        case "js_code": {
            // answer = { code, stdout } (le code EST executé côté client par le
            // worker Pyodide, stdout capturé). On corrige sur la SORTIE attendue
            // (solution.stdout) et/ou des motifs de code (include/forbid).
            const a = answer || {};
            if (s.stdout != null) {
                // `solution.strict` : la sortie doit être reproduite au
                // caractère près (leçon dont la casse ou la ponctuation EST la
                // notion). Sinon on juge le fond, pas la majuscule.
                const strict = s.strict === true;
                if (
                    normalizeOutput(a.stdout, strict) !==
                    normalizeOutput(s.stdout, strict)
                ) {
                    return false;
                }
            }
            const code = normalizeCode(a.code);
            const inc = s.include || [];
            const forbid = (s.forbid || []).map(normalizeCode);
            if (!inc.every((m) => codeSatisfiesPattern(code, m))) {
                return false;
            }
            if (forbid.some((m) => m && codeIncludesPattern(code, m))) {
                return false;
            }
            return s.stdout != null || inc.length > 0;
        }
        // sql_code : la requête est EXÉCUTÉE par le client (sql.js), avec la
        // requête de référence (solution.query) sur une base neuve, et le
        // client compare les deux tableaux (RepsSql.executer). La prod n'a pas
        // SQLite (fait 13 de curriculum/sql/PLAN.md) : les trois correcteurs
        // font donc confiance à ce verdict, `answer.match`, comme ils font
        // confiance au `stdout` envoyé pour python_code. Ce qu'ils vérifient
        // eux-mêmes, à l'identique : les motifs include/forbid, et le refus
        // d'un texte entre guillemets doubles (faits 2 et 16), que SQLite
        // accepte en silence. answer = { code, match }.
        case "sql_code": {
            const a = answer && typeof answer === "object" ? answer : {};
            const brut = typeof a.code === "string" ? a.code : "";
            if (sqlHasDoubleQuotes(brut)) return false;
            const aReference = typeof s.query === "string" && s.query.trim() !== "";
            if (aReference && a.match !== true) return false;
            const code = normalizeCode(brut);
            const inc = s.include || [];
            const forbid = (s.forbid || []).map(normalizeCode);
            if (!inc.every((m) => codeSatisfiesPattern(code, m))) {
                return false;
            }
            if (forbid.some((m) => m && codeIncludesPattern(code, m))) {
                return false;
            }
            return aReference || inc.length > 0;
        }
        case "tap_target": {
            // answer = index RÉEL du segment touché (data.parts). Une cible
            // unique (solution.index) ou plusieurs valides (solution.accept).
            // Miroir EXACT de RepsGrader::grade (PHP, cas 'tap_target').
            if (Array.isArray(s.accept) && s.accept.length) {
                return s.accept.some((v) => indexEq(answer, v));
            }
            return indexEq(answer, s.index);
        }
        case "tune": {
            // answer = objet { clé: valeur courante } des réglages manipulés.
            // On valide chaque réglage attendu (solution.values) : numérique à
            // ±tolérance (solution.tolerance, défaut 0), sinon égalité de chaîne
            // exacte (valeurs enum choisies dans une liste fermée). Tous doivent
            // passer. Miroir EXACT de RepsGrader::grade (PHP, cas 'tune').
            if (!answer || typeof answer !== "object" || Array.isArray(answer)) {
                return false;
            }
            const values = s.values || {};
            const tol = s.tolerance || {};
            const keys = Object.keys(values);
            if (keys.length === 0) return false;
            return keys.every((k) => {
                const expected = values[k];
                const got = answer[k];
                if (isNumericLike(expected)) {
                    if (!isNumericLike(got)) return false;
                    const t = isNumericLike(tol[k]) ? Math.abs(Number(tol[k])) : 0;
                    return Math.abs(Number(got) - Number(expected)) <= t;
                }
                return String(got) === String(expected);
            });
        }
        default:
            return false;
    }
}
